import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Button,
   FormControl,
   FormLabel,
   FormErrorMessage,
   Input,
   Select,
   Grid,
   GridItem,
   Box,
   Text,
   IconButton,
   useToast,
   useColorModeValue,
   AlertDialog,
   AlertDialogOverlay,
   AlertDialogContent,
   AlertDialogHeader,
   AlertDialogBody,
   AlertDialogFooter,
   useColorMode,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { getMainAndSubHsn, addMainAndSubHsn, updateMainAndSubHsn } from '../../../../../redux/actions/mainAndSubHsn';
import { deleteHsnSub } from '../../../../../redux/actions/hsnSub';
import { DynamicInput } from 'components/UIcomponents/Dynamicinput';
import { renderDynamicDropdown } from 'components/UIcomponents/DynamicDropdown';
import { useSelector } from 'react-redux';
import { getTaxGroup } from '../../../../../redux/actions/taxGroup';
import { getHsn } from '../../../../../redux/actions/hsn';
import { renderDynamicDropdown as renderDynamicUiDropdown } from 'components/ui/DynamicDropdown';
import { getShareCompanyOptions } from '../../../../../utils/util';
import TextareaField from 'components/ui/TextareaField';
import { useGlobal, useUser } from 'contexts';
import { getItem } from "../../../../../redux/actions/item"

// Validation schemas remain the same
const validationSchema = Yup.object().shape({
   hsn: Yup.string().required('HSN is required').min(1, 'HSN must be at least 3 characters long'),
   gstType: Yup.string().required('GST Type is required'),
   applyDate: Yup.date().required('Apply Date is required').nullable().typeError('Please enter a valid date'),
   subHsn: Yup.array()
      .of(
         Yup.object().shape({
            taxGroupId: Yup.number().required('Tax Group is required'),
            applyDate: Yup.date().required('Apply Date is required').nullable().typeError('Please enter a valid date'),
         }),
      )
      .min(1, 'At least one Sub HSN is required')
      .test('unique-rates', 'Duplicate From Rate and To Rate are not allowed', function (subHsn) {
         if (!subHsn) return true;

         const seenRates = new Set();

         for (const item of subHsn) {
            const rateKey = `${item.fromRate}-${item.toRate}`;
            if (seenRates.has(rateKey)) {
               return false; // Duplicate found
            }
            seenRates.add(rateKey);
         }

         return true;
      })
      .test('unique-dates', 'Duplicate dates are not allowed', function (value) {
         if (!value) return true;
         const dates = value.map((item) => item.applyDate);
         const uniqueDates = new Set(dates);
         return dates.length === uniqueDates.size;
      }),
});

const HsnMasterForm = ({
   isOpen,
   onClose,
   isEditMode = false,
   demoname,
   currentRow = null,
   saveandcontinue = true,
}) => {
   const [formData, setFormData] = useState({
      hsn: '',
      applyDate: new Date().toISOString().split('T')[0],
      gstType: 'G',
      description: '',
      subHsn: [
         {
            taxGroupId: '',
            fromRate: '',
            toRate: '',
            applyDate: new Date().toISOString().split('T')[0],
         },
      ],
   });
   const { companyList, company, userGlobalSettingsData } = useGlobal();
   const { user } = useUser();
   const dbName = company?.dbName;
   const handleCompanyChange = useCallback((selectedOptions) => {
      setFormData((prev) => ({
         ...prev,
         compIds: selectedOptions ?? [],
      }));
   }, []);
   const companyOptions = useMemo(() => {
      return getShareCompanyOptions({ dbName, company: companyList });
   }, [companyList, dbName]);

   const lastcompanyOptions = useMemo(
      () =>
         companyOptions.map((company) => ({
            value: company.value,
            label: company.label,
            id: company.value,
            name: company.label,
         })),
      [companyOptions],
   );

   const isItem = useMemo(() => {
      return userGlobalSettingsData?.[0]?.askCompany?.item || false;
   }, [userGlobalSettingsData]);

   const [errors, setErrors] = useState({});
   const toast = useToast();
   const dispatch = useDispatch();
   const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   const [deleteItemId, setDeleteItemId] = useState(null);
   const cancelRef = React.useRef();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   // Tailwind-inspired colors with dark mode support
   const themeColors = {
      // Background colors
      bgPrimary: isDark ? 'bg-gray-900' : 'bg-white',
      bgSecondary: isDark ? 'bg-gray-800' : 'bg-gray-50',
      bgAccent: isDark ? 'bg-blue-900/20' : 'bg-blue-50',

      // Text colors
      textPrimary: isDark ? 'text-white' : 'text-gray-900',
      textSecondary: isDark ? 'text-gray-300' : 'text-gray-600',
      textAccent: isDark ? 'text-blue-300' : 'text-blue-600',

      // Border colors
      borderPrimary: isDark ? 'border-gray-700' : 'border-gray-200',
      borderAccent: isDark ? 'border-blue-500/30' : 'border-blue-200',
      borderFocus: isDark ? 'border-blue-400' : 'border-blue-500',

      // Button colors
      btnPrimary: isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600',
      btnSuccess: isDark ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600',
      btnDanger: isDark ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600',
      btnGhost: isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100',

      // Header colors - Special gradient backgrounds
      headerBg: isDark
         ? 'bg-gradient-to-r from-gray-800 via-blue-900/20 to-gray-800 border-b border-gray-700'
         : 'bg-gradient-to-r from-blue-50 via-white to-blue-50 border-b border-blue-200',
      subHeaderBg: isDark
         ? 'bg-gradient-to-r from-gray-800 via-purple-900/20 to-gray-800'
         : 'bg-gradient-to-r from-purple-50 via-white to-purple-50 border-b border-purple-200',
   };

   const taxGroupData = useSelector((state) => state.taxGroup?.list);

   useEffect(() => {
      dispatch(getTaxGroup());
   }, [dispatch]);

   useEffect(() => {
      if (isEditMode && currentRow) {
         const transformedSubHsn =
            currentRow.subHsn?.map((item) => ({
               id: item.id,
               taxGroupId: item.taxGroupId,
               fromRate: item.fromRate,
               toRate: item.toRate,
               applyDate: item.applyDate?.split('T')[0],
            })) || [];

         setFormData({
            id: currentRow.id,
            hsn: currentRow.hsn || '',
            applyDate: currentRow.hmApplyDate
               ? currentRow.hmApplyDate.split('T')[0]
               : new Date().toISOString().split('T')[0],
            gstType: currentRow.gstType || 'G',
            description: currentRow.description || '',
            subHsn:
               transformedSubHsn.length > 0
                  ? transformedSubHsn
                  : [
                     {
                        taxGroupId: '',
                        fromRate: '',
                        toRate: '',
                        applyDate: new Date().toISOString().split('T')[0],
                     },
                  ],
         });
      } else {
         setFormData({
            hsn: '',
            applyDate: new Date().toISOString().split('T')[0],
            gstType: 'G',
            description: '',
            subHsn: [
               {
                  taxGroupId: '',
                  fromRate: '',
                  toRate: '',
                  applyDate: new Date().toISOString().split('T')[0],
               },
            ],
         });
      }
   }, [isEditMode, currentRow]);

   const clearFormData = () => {
      setFormData({
         hsn: '',
         applyDate: new Date().toISOString().split('T')[0],
         gstType: 'G',
         description: '',
         subHsn: [
            {
               taxGroupId: '',
               fromRate: '',
               toRate: '',
               applyDate: new Date().toISOString().split('T')[0],
            },
         ],
      });
      setErrors({});
   };

   const validateForm = async () => {
      try {
         await validationSchema.validate(formData, { abortEarly: false });
         setErrors({});
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const newErrors = {};
            err.inner.forEach((error) => {
               const path = error.path.split('.'); // Splitting path

               if (path[0] === 'subHsn') {
                  const index = Number(path[1]); // Getting subHsn array index
                  const field = path[2]; // Getting the field name

                  if (!newErrors.subHsn) newErrors.subHsn = [];
                  if (!newErrors.subHsn[index]) newErrors.subHsn[index] = {}; // Initialize index if not set

                  newErrors.subHsn[index][field] = error.message; // Assign error message
               } else {
                  newErrors[error.path] = error.message; // Assign general errors
               }
            });

            setErrors(newErrors);
            return false;
         }
         return false;
      }
   };

   const handleInputChange = (field, value) => {
      setFormData((prev) => ({
         ...prev,
         [field]: value,
      }));
   };

   const handleSubHsnChange = (index, field, value) => {
      setFormData((prev) => {
         const newSubHsn = [...prev.subHsn];
         newSubHsn[index] = { ...newSubHsn[index], [field]: value };

         // Remove validation error if field is updated correctly
         setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            if (newErrors.subHsn?.[index]?.[field]) {
               delete newErrors.subHsn[index][field];
            }
            return newErrors;
         });

         return { ...prev, subHsn: newSubHsn };
      });
   };

   const addSubHsn = () => {
      setFormData((prev) => ({
         ...prev,
         subHsn: [
            ...prev.subHsn,
            {
               taxGroupId: '',
               fromRate: '',
               toRate: '',
               applyDate: new Date().toISOString().split('T')[0],
            },
         ],
      }));
   };

   const handlesavecontinue = async () => {
      const isValid = await validateForm();
      if (!isValid) {
         if (formData.hsn === '') {
            hsnRef.current?.focus();
         } else if (formData.applyDate === '') {
            applyDateRef.current?.focus();
         } else if (formData.gstType === '') {
            gstTypeRef.current?.focus();
         } else if (formData.subHsn[0].taxGroupId === '') {
            taxGroupIdRef.current[0]?.focus();
         } else if (formData.subHsn[0].applyDate === '') {
            inapplyDateRef.current[0]?.focus();
         }
         toast({
            title: 'Validation Error',
            description: 'Please check all required fields',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }
      setIsLoading(true);

      const processedData = {
         ...formData,
         compIds: formData.compIds,
         subHsn: formData.subHsn?.map((item) => ({
            ...item,
            fromRate: Number(item.fromRate),
            toRate: Number(item.toRate),
            taxGroupId: Number(item.taxGroupId),
         })),
      };

      try {
         if (isEditMode && currentRow?.id) {
            await dispatch(updateMainAndSubHsn(processedData));
            await dispatch(getMainAndSubHsn());
         } else {
            await dispatch(addMainAndSubHsn(processedData));
            await dispatch(getMainAndSubHsn());
         }
         dispatch(getMainAndSubHsn());
         if (user?.loginhistory?.companyId) {
            const payload = { companyId: user?.loginhistory?.companyId, sortBy: 'name', sortOrder: 'asc' };
            if (isItem) {
               payload.companyIds = user?.loginhistory?.companyId;
            }
            dispatch(getItem(payload));
         }
         clearFormData();
         setIsLoading(false);
      } catch (error) {
         toast({
            title: 'Error',
            description: error.message || 'An error occurred while saving',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         setIsLoading(false);
      }
   };

   const handleSubmit = async () => {
      const isValid = await validateForm();
      if (!isValid) {
         if (formData.hsn === '') {
            hsnRef.current?.focus();
         } else if (formData.applyDate === '') {
            applyDateRef.current?.focus();
         } else if (formData.gstType === '') {
            gstTypeRef.current?.focus();
         } else if (formData.subHsn[0].taxGroupId === '') {
            taxGroupIdRef.current[0]?.focus();
         } else if (formData.subHsn[0].applyDate === '') {
            inapplyDateRef.current[0]?.focus();
         }
         toast({
            title: 'Validation Error',
            description: 'Please check all required fields',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      setIsLoading(true);

      const processedData = {
         ...formData,
         subHsn: formData.subHsn?.map((item) => ({
            ...item,
            fromRate: Number(item.fromRate),
            toRate: Number(item.toRate),
            taxGroupId: Number(item.taxGroupId),
         })),
      };

      try {
         if (isEditMode && currentRow?.id) {
            await dispatch(updateMainAndSubHsn(processedData));
            await dispatch(getMainAndSubHsn());
         } else {
            await dispatch(addMainAndSubHsn(processedData));
            await dispatch(getMainAndSubHsn());
         }
         dispatch(getMainAndSubHsn());
         if (user?.loginhistory?.companyId) {
            const payload = { companyId: user?.loginhistory?.companyId, sortBy: 'name', sortOrder: 'asc' };
            if (isItem) {
               payload.companyIds = user?.loginhistory?.companyId;
            }
            dispatch(getItem(payload));
         }
         onClose();
         setIsLoading(false);
      } catch (error) {
         toast({
            title: 'Error',
            description: error.message || 'An error occurred while saving',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         setIsLoading(false);
      }
   };

   const handleDeleteSubHsn = async (id) => {
      if (!id) {
         toast({
            title: 'Error',
            description: 'Invalid Sub HSN selected for deletion',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      try {
         await dispatch(deleteHsnSub(id));
         // Update local state after successful deletion
         setFormData((prev) => ({
            ...prev,
            subHsn: prev.subHsn.filter((item) => item.id !== id),
         }));
         setIsDeleteAlertOpen(false);
      } catch (error) {
         toast({
            title: 'Error',
            description: 'Failed to delete Sub HSN. Please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const onDeleteClick = (id) => {
      setDeleteItemId(id);
      setIsDeleteAlertOpen(true);
   };

   const removeSubHsn = (index) => {
      const subHsnItem = formData.subHsn[index];

      if (subHsnItem.id) {
         // If it's an existing Sub HSN, show delete confirmation
         onDeleteClick(subHsnItem.id);
      } else {
         // If it's a new Sub HSN, just remove from local state
         setFormData((prev) => ({
            ...prev,
            subHsn: prev.subHsn.filter((_, i) => i !== index),
         }));
      }
   };
   const taxtGroupoption = taxGroupData.map((taxGroup) => ({
      label: taxGroup.name,
      value: taxGroup.id,
      name: taxGroup.name,
      id: taxGroup.id,
   }));

   const handleCansel = async () => {
      onClose();
      if (!isEditMode) {
         clearFormData();
      }
   };

   useEffect(() => {
      if (demoname && demoname !== '') {
         handleInputChange('hsn', demoname);
      }
      demoname = '';
   }, [demoname, isOpen]);

   const hsnRef = useRef(null);
   const applyDateRef = useRef(null);
   const gstTypeRef = useRef(null);
   const taxGroupIdRef = useRef([]);
   const fromRateRef = useRef([]);
   const toRateRef = useRef([]);
   const savefinishRef = useRef([]);
   const savecontinueRef = useRef([]);
   const descriptionRef = useRef(null);
   const inapplyDateRef = useRef([]);
   const addRef = useRef(null);

   useEffect(() => {
      setTimeout(() => {
         hsnRef.current?.focus();
      }, 100); // small delay sometimes helps
   }, [isOpen]);

   useEffect(() => {
      const handleKeyDown = (e) => {
         const activeElement = document.activeElement;
         const isTyping =
            activeElement?.tagName === 'INPUT' ||
            activeElement?.tagName === 'TEXTAREA' ||
            activeElement?.getAttribute('role') === 'textbox' || // for react-select input
            activeElement?.isContentEditable;

         if (isTyping) return; // Don't trigger shortcuts while typing

         if (e.key.toLowerCase() === 's') {
            if (e.shiftKey) {
               handlesavecontinue(); // SHIFT + S
            } else {
               handleSubmit(); // S only
            }
         }
         if (e.key.toLowerCase() === 'c') {
            handleCansel();
         }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, [validateForm]);

   useEffect(() => {
      const handleEsc = (e) => {
         if (e.key === 'Escape') {
            e.preventDefault();
            handleCansel();
         }
      };

      document.addEventListener('keydown', handleEsc);
      return () => {
         document.removeEventListener('keydown', handleEsc);
      };
   }, [handleCansel]);

   return (
      <>
         <Modal isOpen={isOpen} onClose={handleCansel} size="4xl">
            <ModalOverlay className="backdrop-blur-sm" />
            <ModalContent
               className={`${themeColors.bgPrimary} rounded-2xl shadow-2xl border ${themeColors.borderPrimary} mx-4 mt-8`}>
               {/* Enhanced Header with Gradient */}
               <ModalHeader
                  className={`${themeColors.headerBg} ${themeColors.textPrimary} rounded-t-2xl py-6 px-8 border-b ${themeColors.borderPrimary}`}>
                  <div className="flex items-center justify-between">
                     <div>
                        <h2 className={`text-2xl font-bold ${themeColors.textPrimary} mb-1`}>
                           {isEditMode ? 'Edit HSN' : 'Add New HSN'}
                        </h2>
                        <p className={`text-sm ${themeColors.textSecondary}`}>
                           {isEditMode ? 'Update existing HSN details' : 'Create new HSN with sub-categories'}
                        </p>
                     </div>
                     <ModalCloseButton
                        className={`rounded-full p-2 ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                           }`}
                     />
                  </div>
               </ModalHeader>

               <ModalBody className="p-8 space-y-8">
                  {/* Main Form Section */}
                  <div className="space-y-6">
                     <h3
                        className={`text-lg font-semibold ${themeColors.textPrimary} pb-2 border-b ${themeColors.borderAccent}`}>
                        Basic Information
                     </h3>

                     <Grid
                        templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', xl: 'repeat(2, 1fr)' }}
                        gap={6}>
                        <GridItem>
                           {DynamicInput(
                              'hsn',
                              'HSN',
                              {
                                 value: formData.hsn,
                                 onChange: (e) => handleInputChange('hsn', e.target.value),
                                 ref: hsnRef,
                                 onKeyDown: (e) => {
                                    if ((e.key === 'Enter' && !e.shiftKey) || (e.key === 'Tab' && !e.shiftKey)) {
                                       e.preventDefault();
                                       applyDateRef.current?.focus();
                                    }
                                 },
                                 className: `w-full rounded-lg border ${themeColors.borderPrimary} ${themeColors.bgPrimary} ${themeColors.textPrimary} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`,
                              },
                              errors.hsn,
                              themeColors.textSecondary,
                              'text',
                              true,
                           )}
                        </GridItem>

                        <GridItem>
                           {DynamicInput(
                              'applyDate',
                              'Apply Date',
                              {
                                 value: formData.applyDate,
                                 onChange: (e) => handleInputChange('applyDate', e.target.value),
                                 ref: applyDateRef,
                                 onKeyDown: (e) => {
                                    if ((e.key === 'Enter' && !e.shiftKey) || (e.key === 'Tab' && !e.shiftKey)) {
                                       e.preventDefault();
                                       gstTypeRef.current?.focus();
                                    }
                                 },
                                 className: `w-full rounded-lg border ${themeColors.borderPrimary} ${themeColors.bgPrimary} ${themeColors.textPrimary} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`,
                              },
                              errors.applyDate,
                              themeColors.textSecondary,
                              'date',
                              true,
                           )}
                        </GridItem>

                        <GridItem>
                           {renderDynamicDropdown(
                              'gstType',
                              'GST Type',
                              [
                                 { id: 'G', value: 'G', name: 'Goods' },
                                 { id: 'S', value: 'S', name: 'Service' },
                              ],
                              {
                                 value: formData.gstType,
                                 onChange: (value) => handleInputChange('gstType', value),
                                 ref: gstTypeRef,
                                 onKeyDown: (e) => {
                                    if ((e.key === 'Enter' && !e.shiftKey) || (e.key === 'Tab' && !e.shiftKey)) {
                                       e.preventDefault();
                                       taxGroupIdRef.current[0]?.focus();
                                    }
                                 },
                                 className: `w-full rounded-lg border ${themeColors.borderPrimary} ${themeColors.bgPrimary} ${themeColors.textPrimary} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`,
                              },
                              errors.gstType,
                              themeColors.textSecondary,
                              true,
                              'Select GST Type',
                           )}
                        </GridItem>
                     </Grid>
                  </div>

                  {/* Sub HSN Section with Enhanced UI */}
                  <div className="space-y-6">
                     <div className={`${themeColors.subHeaderBg} rounded-lg p-4 border ${themeColors.borderAccent}`}>
                        <div className="flex justify-between items-center">
                           <div>
                              <h3 className={`text-lg font-semibold ${themeColors.textPrimary}`}>Sub HSN Details</h3>
                              <p className={`text-sm ${themeColors.textSecondary} mt-1`}>
                                 Add tax rates and effective dates for different scenarios
                              </p>
                           </div>
                           <Button
                              leftIcon={<AddIcon />}
                              ref={addRef}
                              onClick={addSubHsn}
                              className={`${themeColors.btnPrimary} text-white rounded-lg px-4 py-2 font-medium transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 shadow-md`}
                              size="sm"
                              onKeyDown={(e) => {
                                 if (e.key === 'Tab') {
                                    e.preventDefault();
                                    if (e.shiftKey) {
                                       inapplyDateRef.current[formData.subHsn.length - 1]?.focus();
                                    } else {
                                       descriptionRef.current?.focus();
                                    }
                                 }
                                 if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addSubHsn();
                                 }
                              }}>
                              Add Sub HSN
                           </Button>
                        </div>
                     </div>

                     {errors.subHsn?._error && (
                        <div className={`rounded-lg border border-red-500 bg-red-500/10 p-4`}>
                           <Text className="text-red-500 font-medium">{errors.subHsn._error}</Text>
                        </div>
                     )}

                     <div className="space-y-4">
                        {formData.subHsn.map((subItem, index) => (
                           <div
                              key={index}
                              className={`rounded-xl border ${themeColors.borderAccent} ${themeColors.bgSecondary} p-6 transition-all duration-200 hover:shadow-lg`}>
                              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
                                 {/* Tax Group */}
                                 <div className="md:col-span-2">
                                    <FormControl isRequired isInvalid={!!errors?.subHsn?.[index]?.taxGroupId}>
                                       {renderDynamicDropdown(
                                          'taxGroupId',
                                          'Tax Group',
                                          taxtGroupoption,
                                          {
                                             value: subItem.taxGroupId,
                                             onChange: (value) => handleSubHsnChange(index, 'taxGroupId', value),
                                             ref: (el) => {
                                                taxGroupIdRef.current[index] = el;
                                             },
                                             onKeyDown: (e) => {
                                                if (
                                                   (e.key === 'Enter' && !e.shiftKey) ||
                                                   (e.key === 'Tab' && !e.shiftKey)
                                                ) {
                                                   e.preventDefault();
                                                   fromRateRef?.current[index]?.focus();
                                                }
                                             },
                                             className: `w-full rounded-lg border ${themeColors.borderPrimary} ${themeColors.bgPrimary} ${themeColors.textPrimary} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`,
                                          },
                                          errors?.subHsn?.[index]?.taxGroupId,
                                          themeColors.textSecondary,
                                          true,
                                          'Select Tax Group',
                                       )}
                                    </FormControl>
                                 </div>

                                 {/* Rate Range */}
                                 <div className="flex space-x-2 md:col-span-2">
                                    <div className="flex-1">
                                       {DynamicInput(
                                          'fromRate',
                                          'From Rate',
                                          {
                                             value: subItem.fromRate,
                                             onChange: (e) => handleSubHsnChange(index, 'fromRate', e.target.value),
                                             ref: (el) => {
                                                fromRateRef.current[index] = el;
                                             },
                                             onKeyDown: (e) => {
                                                if (
                                                   (e.key === 'Enter' && !e.shiftKey) ||
                                                   (e.key === 'Tab' && !e.shiftKey)
                                                ) {
                                                   e.preventDefault();
                                                   toRateRef.current[index]?.focus();
                                                }
                                             },
                                             className: `w-full rounded-lg border ${themeColors.borderPrimary} ${themeColors.bgPrimary} ${themeColors.textPrimary} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`,
                                          },
                                          errors?.subHsn?.[index]?.fromRate,
                                          themeColors.textSecondary,
                                          'number',
                                          false,
                                       )}
                                    </div>
                                    <div className="flex-1">
                                       {DynamicInput(
                                          'toRate',
                                          'To Rate',
                                          {
                                             value: subItem.toRate,
                                             onChange: (e) => handleSubHsnChange(index, 'toRate', e.target.value),
                                             ref: (el) => {
                                                toRateRef.current[index] = el;
                                             },
                                             onKeyDown: (e) => {
                                                if (
                                                   (e.key === 'Enter' && !e.shiftKey) ||
                                                   (e.key === 'Tab' && !e.shiftKey)
                                                ) {
                                                   e.preventDefault();
                                                   inapplyDateRef.current[index]?.focus();
                                                }
                                             },
                                             className: `w-full rounded-lg border ${themeColors.borderPrimary} ${themeColors.bgPrimary} ${themeColors.textPrimary} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`,
                                          },
                                          errors?.subHsn?.[index]?.toRate,
                                          themeColors.textSecondary,
                                          'number',
                                          false,
                                       )}
                                    </div>
                                 </div>

                                 {/* Apply Date */}
                                 <div className="md:col-span-1">
                                    {DynamicInput(
                                       'applyDate',
                                       'Apply Date',
                                       {
                                          value: subItem.applyDate,
                                          onChange: (e) => handleSubHsnChange(index, 'applyDate', e.target.value),
                                          ref: (el) => {
                                             inapplyDateRef.current[index] = el;
                                          },
                                          onKeyDown: (e) => {
                                             if (
                                                (e.key === 'Enter' && !e.shiftKey) ||
                                                (e.key === 'Tab' && !e.shiftKey)
                                             ) {
                                                e.preventDefault();
                                                if (index === formData.subHsn.length - 1) {
                                                   addRef.current?.focus();
                                                } else {
                                                   taxGroupIdRef.current[index + 1]?.focus();
                                                }
                                             }
                                          },
                                          className: `w-full rounded-lg border ${themeColors.borderPrimary} ${themeColors.bgPrimary} ${themeColors.textPrimary} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`,
                                       },
                                       errors?.subHsn?.[index]?.applyDate,
                                       themeColors.textSecondary,
                                       'date',
                                       true,
                                    )}
                                 </div>

                                 {/* Delete Button */}
                                 {index > 0 && (
                                    <div className="md:col-span-5 flex justify-end pt-2">
                                       <IconButton
                                          aria-label="Remove Sub HSN"
                                          icon={<DeleteIcon />}
                                          onClick={() => removeSubHsn(index)}
                                          className={`${themeColors.btnDanger} text-white rounded-lg transition-all duration-200 transform hover:scale-110 focus:ring-2 focus:ring-red-500`}
                                          size="sm"
                                       />
                                    </div>
                                 )}
                              </div>
                           </div>
                        ))}
                     </div>
                     <div>
                        <GridItem w="100%" colSpan={2}>
                           <div className="space-y-2">
                              <label
                                 className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                 Notes
                              </label>
                              <TextareaField
                                 ref={descriptionRef}
                                 name="description"
                                 value={formData?.description}
                                 onChange={(e) => handleInputChange('description', e.target.value)}
                                 onKeyDown={(e) => {
                                    if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
                                       e.preventDefault();
                                       if (savefinishRef?.current) {
                                          savefinishRef.current.focus();
                                       }
                                    }
                                 }}
                                 className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none min-h-[100px] resize-y ${isDark
                                    ? 'bg-slate-900/50 border-slate-700 text-white placeholder-gray-500 hover:border-slate-600 focus:border-blue-500 focus:bg-slate-900'
                                    : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 hover:border-gray-300 focus:border-blue-400 focus:bg-white'
                                    }`}
                              />
                           </div>
                        </GridItem>
                     </div>
                  </div>
               </ModalBody>

               {/* Enhanced Footer */}
               <ModalFooter
                  className={`relative rounded-b-3xl ${isDark
                     ? 'bg-gradient-to-r from-gray-900/80 to-gray-800/80 border-t border-gray-700'
                     : 'bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200'
                     }`}
                  py={6}
                  px={8}>
                  <div className="flex gap-3">
                     {!isEditMode && saveandcontinue === true && (
                        <Button
                           ref={savecontinueRef}
                           onClick={handlesavecontinue}
                           isLoading={isLoading}
                           disabled={isLoading}
                           className={`group relative overflow-hidden transition-all duration-300 ${isDark
                              ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 border border-emerald-500 shadow-lg shadow-emerald-900/30'
                              : 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 border border-emerald-400 shadow-lg shadow-emerald-300/30'
                              } text-white font-semibold`}
                           borderRadius="xl"
                           px={8}
                           py={3}
                           _focus={{ boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.4)' }}
                           _hover={{ transform: 'translateY(-2px)' }}
                           onKeyDown={(e) => {
                              if (e.key === 'ArrowRight') {
                                 e.preventDefault();
                                 savefinishRef.current?.focus();
                              }
                           }}>
                           <span className="relative z-10 flex items-center gap-2">
                              <span>💾</span> Save & Continue
                           </span>
                           <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                        </Button>
                     )}

                     <Button
                        onClick={handleSubmit}
                        isLoading={isLoading}
                        disabled={isLoading}
                        ref={savefinishRef}
                        className={`group relative overflow-hidden transition-all duration-300 ${isDark
                           ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 border border-blue-500 shadow-lg shadow-blue-900/30'
                           : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 border border-blue-400 shadow-lg shadow-blue-300/30'
                           } text-white font-semibold`}
                        borderRadius="xl"
                        px={8}
                        py={3}
                        _focus={{ boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.4)' }}
                        _hover={{ transform: 'translateY(-2px)' }}
                        onKeyDown={(e) => {
                           if (e.key === 'ArrowRight') {
                              e.preventDefault();
                              cancelRef.current?.focus();
                           } else if (e.key === 'ArrowLeft') {
                              e.preventDefault();
                              if (!isEditMode) {
                                 savecontinueRef.current?.focus();
                              }
                           }
                        }}>
                        <span className="relative z-10 flex items-center gap-2">
                           <span>✓</span> Save & Finish
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                     </Button>

                     <Button
                        ref={cancelRef}
                        variant="ghost"
                        onClick={onClose}
                        className={`transition-all duration-300 ${isDark
                           ? 'text-gray-300 hover:text-white hover:bg-red-600/20 border border-gray-600 hover:border-red-500'
                           : 'text-gray-700 hover:text-gray-900 hover:bg-red-50 border border-gray-300 hover:border-red-300'
                           } font-semibold`}
                        borderRadius="xl"
                        px={8}
                        py={3}
                        _focus={{ boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.4)' }}
                        _hover={{ transform: 'translateY(-2px)' }}
                        onKeyDown={(e) => {
                           if (e.key === 'ArrowLeft') {
                              e.preventDefault();
                              savefinishRef.current?.focus();
                           }
                        }}>
                        <span className="flex items-center gap-2">
                           <span>✕</span> Cancel
                        </span>
                     </Button>
                  </div>
               </ModalFooter>
            </ModalContent>
         </Modal>

         {/* Enhanced Delete Confirmation Dialog */}
         <AlertDialog
            isOpen={isDeleteAlertOpen}
            leastDestructiveRef={cancelRef}
            onClose={() => setIsDeleteAlertOpen(false)}>
            <AlertDialogOverlay className="backdrop-blur-sm" />
            <AlertDialogContent
               className={`${themeColors.bgPrimary} rounded-2xl border ${themeColors.borderPrimary} shadow-2xl`}>
               <AlertDialogHeader
                  className={`${themeColors.textPrimary} text-xl font-bold border-b ${themeColors.borderPrimary} pb-4`}>
                  Delete Sub HSN
               </AlertDialogHeader>

               <AlertDialogBody className={`${themeColors.textSecondary} py-6`}>
                  Are you sure you want to delete this Sub HSN? This action cannot be undone and may affect existing
                  records.
               </AlertDialogBody>

               <AlertDialogFooter className="border-t ${themeColors.borderPrimary} pt-4">
                  <Button
                     ref={cancelRef}
                     onClick={() => setIsDeleteAlertOpen(false)}
                     className={`${themeColors.btnGhost} rounded-lg px-6 py-2 font-medium transition-all duration-200 border ${themeColors.borderPrimary} hover:shadow-md`}>
                     Cancel
                  </Button>
                  <Button
                     colorScheme="red"
                     onClick={() => handleDeleteSubHsn(deleteItemId)}
                     ml={3}
                     className={`${themeColors.btnDanger} text-white rounded-lg px-6 py-2 font-medium transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 shadow-md`}>
                     Delete
                  </Button>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </>
   );
};

export default HsnMasterForm;
